import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "react-bootstrap/Card";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import RoomIcon from "@mui/icons-material/Room";
import ArrowCircleLeftRoundedIcon from "@mui/icons-material/ArrowCircleLeftRounded";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import "styles/patient-form.scss";
import PatientFormService from "services/PatientFormService.js";
import FamilyService from "services/FamilyService.js";
import FormUtils from "utils/FormUtils.js";
import EnumUtils from "utils/EnumUtils.js";
import { CardActionArea } from "@mui/material";
import useAPI from "hooks/useAPI.js";

const PatientFormsList = () => {
  const navigate = useNavigate();
  const { intakeId, patientId } = useParams();
  const [formsList, setFormsList] = useState([]);

  const [patientDetails, setPatientDetails] = useState({
    patientId: "",
    patientName: "",
    hospitalName: "",
    ageGroup: "",
  });

  const getMemberBasicDetailsAPi = useAPI(
    FamilyService.getMemberBasicDetails,
    getMemberBasicDetailsHandler
  );
  const getFormsAPI = useAPI(
    PatientFormService.getIntakeForms,
    getFormsHandler
  );

  function getMemberBasicDetails() {
    getMemberBasicDetailsAPi.execute(patientId);
  }

  function getForms() {
    getFormsAPI.execute(patientId, intakeId);
  }

  useEffect(() => {
    getMemberBasicDetails();
    getForms();
  }, []);


  /* API Success Handlers */
  function getMemberBasicDetailsHandler(response) {
    let data = response.data;
    setPatientDetails(data);
  }

  function getFormsHandler(response) {
    setFormsList(response.data || []);
  }

  // Define consent form types
  const CONSENT_FORM_TYPES = [
    "PATIENT_CONSENT",
    "NEURODEVELOPMENTAL_SERVICES_CONSENT",
    "LENS_NFB_CONSENT",
    "QEEG_EQUIP_CONSENT"
  ];

  // Logic to separate active and historical forms
  const getProcessedForms = () => {
    let activeForms = [];
    let historicalForms = [];

    // Group forms by type
    const formsByType = {};
    formsList.forEach(form => {
      if (!formsByType[form.registrationFormTypeEnum]) {
        formsByType[form.registrationFormTypeEnum] = [];
      }
      formsByType[form.registrationFormTypeEnum].push(form);
    });

    // Process groups
    Object.keys(formsByType).forEach(type => {
      const forms = formsByType[type];

      // If it's a consent form and there are multiple, sort and split
      if (CONSENT_FORM_TYPES.includes(type) && forms.length > 0) {
        // Sort by ID descending (assuming higher ID is newer)
        // If createdAt is available, it would be better, but ID is a safe proxy for insertion order
        const sortedForms = forms.sort((a, b) => b.id - a.id);

        // Latest is active
        activeForms.push(sortedForms[0]);

        // Others are historical
        if (sortedForms.length > 1) {
          historicalForms.push(...sortedForms.slice(1));
        }
      } else {
        // Not a consent form, or single consent form -> all active
        activeForms.push(...forms);
      }
    });

    // Sort active forms by ID or Name to maintain some order if needed
    activeForms.sort((a, b) => a.id - b.id);

    return { activeForms, historicalForms };
  };

  const { activeForms, historicalForms } = getProcessedForms();

  const submittedCount = activeForms.filter((form) => form.formStatusTypeEnum === "SUBMITTED").length;
  const totalCount = activeForms.length;
  const progressPct = totalCount > 0 ? Math.round((submittedCount / totalCount) * 100) : 0;
  const nextIncompleteForm = activeForms.find((form) => form.formStatusTypeEnum !== "SUBMITTED");

  const getStatusMeta = (status) => {
    if (status === "SUBMITTED") {
      return { label: "Submitted", className: "status-badge submitted" };
    }
    if (status === "PENDING" || status === "IN_PROGRESS" || status === "DRAFT") {
      return { label: "Pending", className: "status-badge pending" };
    }
    return { label: "Not started", className: "status-badge not-started" };
  };

  const renderFormCard = (form, index, highlighted = false) => {
    const statusMeta = getStatusMeta(form.formStatusTypeEnum);
    return (
    <Card
      key={form.id || index}
      className={`card card-table-width auto-height patient-form-card ${highlighted ? "next-form-highlight" : ""} ${CONSENT_FORM_TYPES.includes(form.registrationFormTypeEnum) && historicalForms.includes(form) ? "historical-form" : ""}`}
      onClick={() =>
        navigateToForm(
          form.registrationFormTypeEnum,
          form.formStatusTypeEnum,
          patientId,
          intakeId,
          form.id
        )
      }
      style={CONSENT_FORM_TYPES.includes(form.registrationFormTypeEnum) && historicalForms.includes(form) ? { opacity: 0.7, backgroundColor: "#f5f5f5" } : {}}
    >
      <CardActionArea>
        <Card.Body>
          <div className="form-card-header">
            <div className="form-title-wrap">
              <Typography className="form-title-text">
                {EnumUtils.parse(form.registrationFormTypeEnum)}
              </Typography>
              <Typography className="form-saved-text">
                Last saved: {form.lastUpdatedAt || "Not started yet"}
              </Typography>
            </div>
            <span className={statusMeta.className}>{statusMeta.label}</span>
          </div>
          <div className="form-card-footer">
            <span className="form-open-text">Open Form</span>
            <ArrowRightIcon className="form-open-arrow" />
          </div>
        </Card.Body>
      </CardActionArea>
    </Card>
  );
  };

  return (<div className="registration-forms-container consultation-forms-page">
    <div className="forms-header-row">
      <Button className="forms-back-btn" startIcon={<ArrowCircleLeftRoundedIcon fontSize="small" />} onClick={() => navigate(-1)}>
        Back
      </Button>
    </div>

    <h2 className="consultation-forms-title">Forms to fill in before consultation</h2>
    <p className="consultation-forms-subtitle">
      Complete the forms below so your consultant is fully prepared before your visit.
    </p>

    <div className="forms-progress-card">
      <div className="forms-progress-top">
        <Typography variant="h6" className="forms-progress-title">{submittedCount} of {totalCount} forms completed</Typography>
        <Typography variant="body2" color="text.secondary">{progressPct}% complete</Typography>
      </div>
      <LinearProgress variant="determinate" value={progressPct} className="forms-progress-bar" />
      {nextIncompleteForm ? (
        <div className="forms-cta-row">
          <Typography variant="body2" color="text.secondary">
            Next up: {EnumUtils.parse(nextIncompleteForm.registrationFormTypeEnum)}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() =>
              navigateToForm(
                nextIncompleteForm.registrationFormTypeEnum,
                nextIncompleteForm.formStatusTypeEnum,
                patientId,
                intakeId,
                nextIncompleteForm.id
              )
            }
          >
            Continue Forms
          </Button>
        </div>
      ) : null}
    </div>

    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        {getFormsAPI.status === "pending" ? (
          <div className="text-center">
            <CircularProgress
              className="text-center"
              sx={{
                mt: 50,
              }}
            />
          </div>
        ) : (
          <>
            <div className="forms-grid">
              {activeForms.map((form, index) => renderFormCard(form, index, nextIncompleteForm?.id === form.id))}
            </div>

            {historicalForms.length > 0 && (
              <div className="forms-history-block">
                <h4 className="forms-history-title">Historical / Previous Versions</h4>
                <div className="forms-grid">
                  {historicalForms.map((form, index) => renderFormCard(form, index))}
                </div>
              </div>
            )}
          </>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Card className="card card-table-width auto-height patient-summary-card">
          <Card.Body>
            <div className="patient-summary-content">
              <Typography variant="h6" className="patient-summary-name">{patientDetails.patientName || "Patient"}</Typography>
              <Typography variant="body2" className="patient-summary-meta">
                Patient ID: {patientDetails.patientId || "-"}
              </Typography>
              <Typography variant="body2" className="patient-summary-meta">
                <RoomIcon fontSize="small" /> {patientDetails.hospitalName || "Centre not assigned"}
              </Typography>
              <Typography variant="body2" className="patient-summary-meta">
                {patientDetails.ageGroup || "Age group not available"}
              </Typography>
            </div>
          </Card.Body>
        </Card>
        <Card
          className="card card-table-width auto-height hover-effect sallie-card"
          onClick={() => window.open("https://melillomethod.powerofpatients.com/signIn", "_blank")}
        >
          <CardActionArea>
            <Card.Body className="text-center">
              <div className="sallie-signup-container">
                <h5 className="gradient-text">Sign up on <i>Sallie</i></h5>
                <p className="small-text">Get access to more resources and track your progress</p>
                <div className="cta-button">
                  Join Now <ArrowRightIcon fontSize="small" />
                </div>
              </div>
            </Card.Body>
          </CardActionArea>
        </Card>

      </Grid>
    </Grid>
  </div>
  );
}


function navigateToForm(
  patientFormTypeEnum,
  formStatus,
  patientId,
  intakeId,
  formId
) {
  let viewMode = "write";
  if (formStatus === "SUBMITTED") viewMode = "read";

  if (patientFormTypeEnum === "DEMOGRAPHIC_INFO_CHECKLIST_CHILD") {
    window.open(
      `user-forms/patient-registration/child-demographic-info-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_CHILD") {
    window.open(
      `user-forms/patient-registration/child-history.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HEMISPHERIC_CHECKLIST_CHILD") {
    window.open(
      `user-forms/patient-registration/child-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "DEMOGRAPHIC_INFO_CHECKLIST_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-demographic-info-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-history.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HEMISPHERIC_CHECKLIST_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "COGNITIVE_STYLE_ASSESSMENT_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-cognitive-style-assessment.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "SENSORY_FUNCTION_CHECKLIST_ADULT") {
    window.open(
      `user-forms/patient-registration/adult-sensory-function-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "PATIENT_CONSENT") {
    window.open(
      `user-forms/patient-registration/patient-consent.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_CHILD_V2") {
    window.open(
      `user-forms/patient-registration/child-history-v2.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "HISTORY_ADULT_V2") {
    window.open(
      `user-forms/patient-registration/adult-history-v2.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "ADULT_FOLLOW_UP") {
    window.open(
      `user-forms/patient-registration/adult-follow-up.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "CHILD_FOLLOW_UP") {
    window.open(
      `user-forms/patient-registration/child-follow-up.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "LENS_NFB_CONSENT") {
    window.open(
      `user-forms/patient-registration/lens-consent.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "NEURODEVELOPMENTAL_SERVICES_CONSENT") {
    window.open(
      `user-forms/patient-registration/neurodevelopmental-consent.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "MOTHER_HEMISPHERIC_CHECKLIST") {
    window.open(
      `user-forms/patient-registration/mother-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else if (patientFormTypeEnum === "FATHER_HEMISPHERIC_CHECKLIST") {
    window.open(
      `user-forms/patient-registration/father-hemispheric-checklist.html?&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=${viewMode}`,
      "_blank"
    );
  } else {
    alert("Invalid Patient Assessment Form Type");
  }
}

export default PatientFormsList;